import { ComponentProps } from "react";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { uniqueId } from "lodash";

import { IconSource } from "../../../../commons/types/icon";
import Toast from "../../components/Toast/Toast";
import { ToastAlignment } from "../../components/ToastLayout/ToastLayout";

export type ToastProps = ComponentProps<typeof Toast>;

export interface ToastActionProps extends Pick<
  ToastProps,
  "id" | "title" | "description" | "timeout" | "kind" | "permanent" | "shouldInteractionLeaveKeyboardOpen" | "onClose"
> {
  icon?: IconSource;
  primaryAction?: { label: string; callback: () => void };
  secondaryAction?: { label: string; callback: () => void };
}

interface StoredToast extends ToastActionProps {
  id: string;
}

interface ToastsSliceState {
  toasts: StoredToast[];
  toastAlignment: ToastAlignment;
}

const initialState: ToastsSliceState = {
  toasts: [],
  toastAlignment: "center"
};

const toastsSlice = createSlice({
  name: "toasts",
  initialState,
  reducers: {
    showToast: {
      reducer: (state, action: PayloadAction<StoredToast>) => {
        // Remove any existing toast with the same id, then add the new one
        state.toasts = state.toasts.filter(({ id }) => id !== action.payload.id);
        state.toasts.push(action.payload);
      },
      prepare: (props: ToastActionProps) => ({
        payload: {
          id: props.id ?? uniqueId(),
          ...props
        }
      })
    },
    hideToast: (state, action: PayloadAction<{ id: string }>) => {
      state.toasts = state.toasts.filter(({ id }) => id !== action.payload.id);
    },
    setToastAlignment: (state, action: PayloadAction<{ toastAlignment: ToastAlignment }>) => {
      state.toastAlignment = action.payload.toastAlignment;
    }
  }
});

export default toastsSlice;
